import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { 
  Plus, TrendingUp, AlertTriangle, DollarSign, 
  BarChart3, ChevronLeft, ChevronRight,
  Building2, Clock, PlayCircle, RefreshCw, List,
  Search, Eye, Calendar, Shield, Zap, Settings, Download, Filter, Target, Trash2, Edit, CheckCircle, XCircle,
  ChevronDown, FileSpreadsheet, FileJson, Repeat, CreditCard, MoreHorizontal, Archive
} from "lucide-react";
import { 
  DirectDebitStats, DirectDebit, DirectDebitFilter, Company
} from "../../types/direct-debit";
import { PageHeader } from "../../components/common/PageHeader";
import { Can } from "../../components/Can";
import { CanAny } from "../../components/CanAny";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { financeApi } from "../../utils/finance-api";
import { toast } from "sonner";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

// Mirrors the settings page gate and its data endpoints: the company
// permission, or the module-level read granted by the finance role.
const SETTINGS_PERMISSIONS = [
  { I: ACTIONS.READ, a: RESOURCES.DIRECT_DEBIT_FINANCE_COMPANY },
  { I: ACTIONS.READ, a: RESOURCES.DIRECT_DEBIT },
];

const DirectDebitRegisterPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<DirectDebitStats | null>(null);
  const [directDebits, setDirectDebits] = useState<DirectDebit[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [trendData, setTrendData] = useState<{ month: string, amount: number }[]>([]);
  const [currentChartYear, setCurrentChartYear] = useState(new Date().getFullYear());
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<DirectDebitFilter>({});

  useEffect(() => {
    loadData();
  }, [currentChartYear, activeTab, filter, searchQuery]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === "dashboard") {
        const [statsRes, trendsRes] = await Promise.all([
          financeApi.getDashboardStats(),
          financeApi.getYearlyTrends(currentChartYear)
        ]);
        setStats(statsRes);
        setTrendData(trendsRes);
      } else if (activeTab === "debits") {
        const [res, companiesRes] = await Promise.all([
          financeApi.listDirectDebits({ ...filter, q: searchQuery }),
          financeApi.listCompanies()
        ]);
        setDirectDebits(res.data);
        setCompanies(companiesRes);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      const params = new URLSearchParams();
      params.append("tab", activeTab);
      params.append("format", format);

      if (activeTab === 'dashboard') {
        params.append('year', currentChartYear.toString());
      } else if (activeTab === 'debits') {
        if (filter.status && filter.status !== 'all') params.append('status', filter.status);
        if (filter.frequency && filter.frequency !== 'all') params.append('frequency', filter.frequency);
        if (filter.companyId) params.append('companyId', filter.companyId);
        if (searchQuery) params.append('q', searchQuery);
      }

      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

      const response = await fetch(`${cleanBaseUrl}/api/finance/direct-debit/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error("Failed to export data");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `direct_debits_export_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to generate export file");
    }
  };
const handleToggleActiveStatus = async (debit: DirectDebit) => {
  try {
    const isCurrentlyActive = debit.status === 'active';
    const newStatus = isCurrentlyActive ? 'suspended' : 'active';

    await financeApi.updateDirectDebit(debit.id, { status: newStatus });

    // Refresh all data (including stats and trends) to ensure the dashboard is in sync
    await loadData();

    toast.success(`Direct debit ${isCurrentlyActive ? 'archived' : 'resumed'} successfully`);
  } catch (error) {
    toast.error("Failed to update direct debit status");
  }
};

  const formatCurrency = (amount: number) => {
    const safeAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(safeAmount);
  };

  const getStatusColor = (status: DirectDebit['status']) => {
    switch (status) {
      case "active": return "bg-green-100 text-green-700 border-green-200";
      case "suspended": return "bg-gray-100 text-gray-700 border-gray-200";
      case "cancelled": return "bg-red-100 text-red-700 border-red-200";
      case "completed": return "bg-blue-100 text-blue-700 border-blue-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getApprovalStatusColor = (status: DirectDebit['approvalStatus']) => {
    switch (status) {
      case "approved": return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "pending": return "bg-amber-100 text-amber-700 border-amber-200";
      case "rejected": return "bg-red-100 text-red-700 border-red-200";
      case "cancelled": return "bg-slate-100 text-slate-700 border-slate-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Direct Debit Register</h1>
          <p className="text-muted-foreground">Manage and track company direct debits</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Desktop Actions */}
          <div className="hidden md:flex gap-2">
            <Can I={ACTIONS.READ} a={RESOURCES.DIRECT_DEBIT}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Download size={16} />
                    <span>Export</span>
                    <ChevronDown size={14} className="opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => handleExport('csv')} className="gap-2 cursor-pointer">
                    <FileSpreadsheet size={16} className="text-slate-500" />
                    <span>Export as CSV</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('excel')} className="gap-2 cursor-pointer">
                    <FileJson size={16} className="text-slate-500" />
                    <span>Export as Excel</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </Can>
            <CanAny of={SETTINGS_PERMISSIONS}>
              <Button variant="outline" onClick={() => navigate('/finance/direct-debit/settings')}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            </CanAny>
            <Can I={ACTIONS.CREATE} a={RESOURCES.DIRECT_DEBIT}>
              <Button onClick={() => navigate('/finance/direct-debit/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Add Debit
              </Button>
            </Can>
          </div>

          {/* Mobile Actions Dropdown */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 border-gray-200">
                  <MoreHorizontal className="h-5 w-5 text-gray-600" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 p-2 space-y-1">
                <Can I={ACTIONS.CREATE} a={RESOURCES.DIRECT_DEBIT}>
                  <DropdownMenuItem onClick={() => navigate('/finance/direct-debit/new')} className="rounded-lg h-11 font-bold gap-3">
                    <Plus className="h-4 w-4 text-blue-600" />
                    Add New Debit
                  </DropdownMenuItem>
                </Can>
                <CanAny of={SETTINGS_PERMISSIONS}>
                  <DropdownMenuItem onClick={() => navigate('/finance/direct-debit/settings')} className="rounded-lg h-11 font-bold gap-3">
                    <Settings className="h-4 w-4 text-slate-600" />
                    Settings
                  </DropdownMenuItem>
                </CanAny>
                <div className="h-px bg-slate-100 my-1" />
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2 py-2">Export Data</p>
                <Can I={ACTIONS.READ} a={RESOURCES.DIRECT_DEBIT}>
                  <DropdownMenuItem onClick={() => handleExport('csv')} className="rounded-lg h-11 font-bold gap-3">
                    <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('excel')} className="rounded-lg h-11 font-bold gap-3">
                    <FileJson className="h-4 w-4 text-indigo-600" />
                    Export as Excel
                  </DropdownMenuItem>
                </Can>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 inline-flex h-10 items-center justify-center rounded-full text-slate-500 mb-6">
          <TabsTrigger 
            value="dashboard" 
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all"
          >
            Dashboard
          </TabsTrigger>
          <TabsTrigger 
            value="debits" 
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all"
          >
            All Debits
          </TabsTrigger>
          <TabsTrigger 
            value="reports" 
            className="px-6 py-2 text-xs font-semibold data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm rounded-full transition-all"
          >
            Reports
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
           {isLoading && !stats ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                <span className="text-gray-500">Loading Dashboard...</span>
              </div>
           ) : (
             <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground font-medium">Active Debits</p>
                        <p className="text-2xl font-semibold">{stats?.totalActiveDebits || 0}</p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-lg">
                        <PlayCircle className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground font-medium">Monthly Total</p>
                        <p className="text-2xl font-semibold">{formatCurrency(stats?.totalMonthlyAmount || 0)}</p>
                      </div>
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <DollarSign className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground font-medium">Pending Approvals</p>
                        <p className="text-2xl font-semibold">{stats?.pendingApprovals || 0}</p>
                      </div>
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <AlertTriangle className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground font-medium">Annual Total</p>
                        <p className="text-2xl font-semibold">{formatCurrency(stats?.totalAnnualAmount || 0)}</p>
                      </div>
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <TrendingUp className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Chart */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
                  <div className="space-y-1">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                      Liability Trends
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">Monthly liability forecast for {currentChartYear}</p>
                  </div>
                  <div className="flex items-center gap-2 border rounded-lg p-1 bg-gray-50">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCurrentChartYear(prev => prev - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-semibold px-2">{currentChartYear}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCurrentChartYear(prev => prev + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px] w-full mt-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                        <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} tickFormatter={(val) => `$${val/1000}k`} />
                        <Tooltip 
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                          formatter={(value: number) => [formatCurrency(value), 'Liability']}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="amount" 
                          stroke="#3b82f6" 
                          strokeWidth={3} 
                          dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }} 
                          activeDot={{ r: 6, strokeWidth: 0 }} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Bottom sections */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By Department */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <BarChart3 className="h-5 w-5 text-blue-600" />
                      Debits by Department
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {stats?.totalByDepartment.map((dept) => (
                        <div key={dept.department} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Building2 className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{dept.department}</p>
                              <p className="text-sm text-muted-foreground">{dept.count} debits</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-slate-900">{formatCurrency(dept.amount)}</p>
                          </div>
                        </div>
                      ))}
                      {stats?.totalByDepartment.length === 0 && (
                        <p className="text-center py-8 text-muted-foreground italic text-sm">No departmental data.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="h-5 w-5 text-blue-600" />
                      Recent Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {stats?.recentActivity.map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            activity.type === 'processed' ? 'bg-green-100' :
                            activity.type === 'approved' ? 'bg-blue-100' :
                            activity.type === 'failed' ? 'bg-red-100' :
                            'bg-gray-100'
                          }`}>
                            {activity.type === 'processed' ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : activity.type === 'approved' ? (
                              <Shield className="h-4 w-4 text-blue-600" />
                            ) : activity.type === 'failed' ? (
                              <XCircle className="h-4 w-4 text-red-600" />
                            ) : (
                              <Clock className="h-4 w-4 text-gray-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900">{activity.description}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{new Date(activity.date).toLocaleDateString()}</span>
                              <span>•</span>
                              <span>{activity.user}</span>
                              {activity.amount && (
                                <>
                                  <span>•</span>
                                  <span>{formatCurrency(activity.amount)}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {stats?.recentActivity.length === 0 && (
                        <p className="text-center py-8 text-muted-foreground italic text-sm">No recent activity.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
             </>
           )}
        </TabsContent>

        <TabsContent value="debits" className="space-y-6">
          {/* Filters Area */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search debits, payees, references..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-slate-200 focus-visible:ring-blue-500"
              />
            </div>
            
            <Select value={filter.frequency || "all"} onValueChange={(value) => setFilter(prev => ({ ...prev, frequency: value as any }))}>
              <SelectTrigger className="w-full sm:w-[160px] bg-white border-slate-200 overflow-hidden">
                <div className="flex items-center gap-2 truncate w-full">
                  <Repeat className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="truncate flex-1 text-left">
                    <SelectValue placeholder="Frequency" />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Frequencies</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="annually">Annually</SelectItem>
                <SelectItem value="ad-hoc">Ad-hoc</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filter.status || "all"} onValueChange={(value) => setFilter(prev => ({ ...prev, status: value as any }))}>
              <SelectTrigger className="w-full sm:w-[130px] bg-white border-slate-200 overflow-hidden">
                <div className="flex items-center gap-2 truncate w-full">
                  <PlayCircle className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="truncate flex-1 text-left">
                    <SelectValue placeholder="Status" />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Inactive</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filter.approvalStatus || "all"} onValueChange={(value) => setFilter(prev => ({ ...prev, approvalStatus: value as any }))}>
              <SelectTrigger className="w-full sm:w-[160px] bg-white border-slate-200 overflow-hidden">
                <div className="flex items-center gap-2 truncate w-full">
                  <Shield className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="truncate flex-1 text-left">
                    <SelectValue placeholder="Approval" />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Approvals</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filter.companyId || "all"} onValueChange={(value) => setFilter(prev => ({ ...prev, companyId: value }))}>
              <SelectTrigger className="w-full sm:w-[160px] bg-white border-slate-200 overflow-hidden">
                <div className="flex items-center gap-2 truncate w-full">
                  <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                  <div className="truncate flex-1 text-left">
                    <SelectValue placeholder="Company" />
                  </div>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
           
           <Card>
             <CardContent className="p-0">
               <Table>
                 <TableHeader>
                   <TableRow>
                     <TableHead className="font-semibold">Description</TableHead>
                     <TableHead className="font-semibold">Company</TableHead>
                     <TableHead className="font-semibold">Payee</TableHead>
                     <TableHead className="font-semibold text-right">Amount</TableHead>
                     <TableHead className="font-semibold text-center">Frequency</TableHead>
                     <TableHead className="font-semibold text-center">Next Debit</TableHead>
                     <TableHead className="font-semibold">Status</TableHead>
                     <TableHead className="font-semibold">Approval</TableHead>
                     <TableHead className="font-semibold text-center">Actions</TableHead>
                   </TableRow>
                 </TableHeader>
                 <TableBody>
                   {isLoading ? (
                      <TableRow><TableCell colSpan={9} className="h-32 text-center"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-blue-600" /></TableCell></TableRow>
                   ) : directDebits.length > 0 ? (
                     directDebits.map((debit) => (
                       <TableRow key={debit.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/finance/direct-debit/${debit.id}`)}>
                         <TableCell className="py-4">
                           <div className="flex items-start gap-3">
                             <div className="p-2 bg-blue-50 rounded-lg mt-1"><Building2 className="h-4 w-4 text-blue-600" /></div>
                             <div>
                               <p className="font-medium text-gray-900">{debit.description}</p>
                               <p className="text-xs text-muted-foreground">{debit.department}</p>
                             </div>
                           </div>
                         </TableCell>
                         <TableCell className="text-sm font-medium">
                           {debit.company?.name || 'Unknown'}
                         </TableCell>
                         <TableCell className="font-medium">{debit.payeeName}</TableCell>
                         <TableCell className="text-right font-semibold">{formatCurrency(debit.monthlyDebitAmount)}</TableCell>
                         <TableCell className="text-center">
                           <span className="capitalize text-sm">{debit.frequency}</span>
                         </TableCell>
                         <TableCell className="text-center text-sm">
                           {debit.status === 'active' && debit.nextScheduledDate ? new Date(debit.nextScheduledDate).toLocaleDateString() : 'N/A'}
                         </TableCell>
                         <TableCell>
                           <Badge variant="outline" className={getStatusColor(debit.status)}>
                             {debit.status === 'suspended' ? 'inactive' : debit.status}
                           </Badge>
                         </TableCell>
                         <TableCell>
                           <Badge variant="outline" className={getApprovalStatusColor(debit.approvalStatus)}>
                             {debit.approvalStatus}
                           </Badge>
                         </TableCell>
                         <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-center gap-2">
                               <Button variant="ghost" size="icon" onClick={() => navigate(`/finance/direct-debit/${debit.id}`)}>
                                 <Eye className="h-4 w-4" />
                               </Button>
                               <Button variant="ghost" size="icon" onClick={() => navigate(`/finance/direct-debit/${debit.id}/edit`)}>
                                 <Edit className="h-4 w-4" />
                               </Button>

                               <DropdownMenu>
                                 <DropdownMenuTrigger asChild>
                                   <Button variant="ghost" size="icon">
                                     <MoreHorizontal className="h-4 w-4" />
                                   </Button>
                                 </DropdownMenuTrigger>
                                 <DropdownMenuContent align="end" className="w-40">
                                   <DropdownMenuItem 
                                     className="cursor-pointer"
                                     onClick={() => handleToggleActiveStatus(debit)}
                                   >
                                     {debit.status === 'active' ? (
                                       <>
                                         <Archive className="h-4 w-4 mr-2" />
                                         Archive
                                       </>
                                     ) : (
                                       <>
                                         <PlayCircle className="h-4 w-4 mr-2" />
                                         Resume
                                       </>
                                     )}
                                   </DropdownMenuItem>
                                 </DropdownMenuContent>                               </DropdownMenu>
                            </div>
                         </TableCell>
                       </TableRow>
                     ))
                   ) : (
                     <TableRow>
                       <TableCell colSpan={9} className="h-64 text-center py-20 text-slate-400 italic">
                         <div className="flex flex-col items-center justify-center opacity-50">
                           <CreditCard className="h-12 w-12 mb-3" />
                           <p>No debits found matching your filters.</p>
                         </div>
                       </TableCell>
                     </TableRow>
                   )}
                 </TableBody>
               </Table>
             </CardContent>
           </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Reports & Analytics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">Generate detailed reports on direct debit activity</p>
              <div className="text-center py-8">
                <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold">Advanced Reporting</h3>
                <p className="text-muted-foreground">Comprehensive analytics and reporting coming soon</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DirectDebitRegisterPage;
